/**
 * ИИ-аналитика: витрина готовых вопросов на реальных числах среза.
 *
 * Форма ответа взята из презентации заказчику (слайды 9–10) дословно:
 * Краткий вывод → Отклонения → Причины → Связанные данные → Рекомендуемые
 * действия. Это раскладка уже посчитанных фактов, и модели она не требует:
 * рецепт считает числа из файлов фикстур, а разметка расставляет их по пяти
 * именованным блокам. Сама разметка живёт в `CaseContour.tsx` (`AnswerView`),
 * чтобы кейс контура и станция печатали ОДНУ форму, а не две похожие.
 *
 * Что осталось строже деки и меняться не должно:
 * — «языковая модель не подключена» стоит в шапке и в каждом ответе;
 * — каждое утверждение несёт эпистемический класс, выданный рецептом;
 * — «Рекомендуемые действия» — ссылки на экраны прототипа с пометкой
 *   «решение за специалистом»; санкций, рейтингов и баллов здесь нет.
 *
 * Тексты: рецепты зовутся при рендере и получают `t` из контекста (`Ctx.t`),
 * чтобы вставлять ссылки тегами ICU (`<link>…</link>`) — порядок слов в
 * переводе свободный, склеенных из кусков фраз здесь нет.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Bug,
  ClipboardList,
  Droplets,
  FileSearch,
  GitCompare,
  Layers,
  ListTree,
  MapPinned,
  MessageSquareOff,
  NotebookPen,
  Scale,
  Search,
  Sparkles,
  Timer,
  TrendingDown,
  type LucideIcon,
} from 'lucide-react';
import { PageHeader } from '../ui/PageHeader';
import { Card, Stack } from '../ui/Card';
import { EmptyState } from '../ui/EmptyState';
import { EvidenceMark } from '../ui/EvidenceMark';
import { Icon } from '../ui/Icon';
import { usePageTrail } from '../shell/ShellContext';
import { useTrackData } from '../data/useTrackData';
import { FILES } from '../data/loader';
import {
  countOf,
  cycleTitle,
  dateLong,
  dateShort,
  ha,
  m3,
  num,
  pct,
  pctAxis,
  share,
  WORDS,
} from '../lib/format';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { runtimeLocale, numberLocale } from '../../i18n/runtime';
import type {
  AttentionItem,
  ContourMeta,
  Crop,
  Cycle,
  Farm,
  Massif,
  Meta,
  Obligation,
  QuarantineFile,
  WaterFile,
  Work,
} from '../data/types';
import {
  AnswerView,
  CaseContour,
  cropAt,
  DERIVED,
  FACT,
  seasonsOf,
  type Answer,
  type AnswerAction,
  type Claim,
} from './CaseContour';
import './pages.css';
import './analytics.css';
import './ops/ops.css';
import { srv } from '../../i18n/serverText';

const AS_OF_FALLBACK = '2026-08-26';
const WEEK_DAYS = 7;
/** Массив с полусотней работ не сравнивается с массивом на девятьсот. */
const COMPARE_FLOOR = 100;
/** Бюджет плотности: сколько объектов печатается в одном отклонении. */
const LIST_LIMIT = 5;
/** Сколько контуров показывает поиск по номеру. */
const PICK_LIMIT = 8;

const intlLocale = () => {
  return numberLocale();
};

/** Число с одним знаком после запятой на языке интерфейса. */
const fmtOne = (value: number): string =>
  new Intl.NumberFormat(intlLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);

function daysBetween(fromIso: string, toIso: string): number {
  const a = Date.parse(`${fromIso}T00:00:00Z`);
  const b = Date.parse(`${toIso}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.round((b - a) / 86_400_000);
}

/* ------------------------------------------------------------------ тексты */

const m = defineMessages({
  /* --- общее --- */
  points: { id: 'explore.common.points', defaultMessage: '{value} п.п.', description: 'Разрыв двух долей в процентных пунктах: «12,3 п.п.»; {value} — число с одним знаком' },
  noteAssumption: { id: 'explore.common.assumptionMark', defaultMessage: 'допущение прототипа', description: 'Метка класса утверждения в ответе аналитики: это допущение, принятое генератором данных' },
  openCycle: { id: 'explore.common.openCycle', defaultMessage: 'Открыть агроцикл «{cycle}»', description: 'Кнопка-ссылка в «Рекомендуемых действиях» ответа аналитики: перейти к агроциклу; {cycle} — «Хлопок 2026»' },
  subjectCycle: { id: 'explore.common.subjectCycle', defaultMessage: 'агроцикл «{cycle}»', description: 'Предмет поручения словами (строчными), когда нет более точного: агроцикл; {cycle} — «Хлопок 2026»' },
  compareTwo: { id: 'explore.common.compareTwo', defaultMessage: 'Сравнить два хозяйства', description: 'Кнопка-ссылка в «Рекомендуемых действиях» ответа аналитики: перейти к сравнению двух хозяйств' },
  compareHint: {
    id: 'explore.common.compareHint',
    defaultMessage: 'пара подставлена по наибольшему числу работ на полях — чтобы знаменатели были сопоставимы; на экране её можно поменять, сводной оценки и рейтинга там нет',
    description: 'Подсказка под кнопкой «Сравнить два хозяйства» (строчными): по какому правилу выбрана пара',
  },
  assignHintSource: {
    id: 'explore.common.assignHintSource',
    defaultMessage: 'адресное задание со сроком и ответственным; статус и срок там ведутся отдельно. Позиция очереди: {title} — подставит заголовок, описание и привязку',
    description: 'Подсказка под кнопкой «Создать поручение» (строчными), когда найдена позиция очереди внимания; {title} — заголовок позиции',
  },
  assignHintNone: {
    id: 'explore.common.assignHintNone',
    defaultMessage: 'адресное задание со сроком и ответственным; статус и срок там ведутся отдельно. Позиции очереди с этим предметом в срезе нет — форма откроется пустой, предмет придётся назвать словами: {subject}',
    description: 'Подсказка под кнопкой «Создать поручение» (строчными), когда позиции очереди нет; {subject} — предмет поручения словами',
  },

  /* --- «Какие массивы отстают по отчётам» --- */
  lagTitle: { id: 'explore.lag.title', defaultMessage: 'Какие массивы отстают по отчётам', description: 'Заголовок ответа аналитики о массивах, отстающих по отчётам' },
  lagAssign: { id: 'explore.lag.assign', defaultMessage: 'Создать поручение по массиву {name}', description: 'Кнопка-ссылка в «Рекомендуемых действиях»: поручение по массиву; {name} — название массива' },
  lagAssignAny: { id: 'explore.lag.assignAny', defaultMessage: 'Создать поручение по массиву из списка', description: 'Кнопка-ссылка в «Рекомендуемых действиях», когда массива нет: поручение по массиву, выбранному в форме' },
  lagSubject: { id: 'explore.lag.subject', defaultMessage: 'массив {name}, агроцикл «{cycle}»', description: 'Предмет поручения словами (строчными): массив и агроцикл; {name} — название массива, {cycle} — «Хлопок 2026»' },
  lagOpenHint: { id: 'explore.lag.openHint', defaultMessage: 'какие именно работы дали остаток без отчёта — по окнам и по хозяйствам', description: 'Подсказка под кнопкой «Открыть агроцикл» в ответе об отстающих массивах (строчными)' },
  lagSummaryOverall: {
    id: 'explore.lag.summary.overall',
    defaultMessage:
      'По району отчитались {share} работ на полях агроцикла «{cycle}» (по открытым окнам: {reported} из {total, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работы на полях}}).',
    description: 'Краткий вывод ответа «Какие массивы отстают по отчётам», первое предложение; {share} — процент, {reported} — число отчитавшихся (строка), {total} — всего работ на полях открытых окон',
  },
  lagSummaryWorst: {
    id: 'explore.lag.summary.worst',
    defaultMessage:
      'Ниже всех держится <link>{name}</link> — {share} из {total, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работы на полях}}{hasGap, select, yes {; разрыв с верхним массивом — {gap}.} other {.}}',
    description: 'Краткий вывод ответа об отстающих массивах, второе предложение; <link> — ссылка на массив, {share} — процент, {total} — работ на полях массива, {hasGap} — yes/no, {gap} — разрыв в п.п.',
  },
  lagDevNoRows: {
    id: 'explore.lag.dev.noRows',
    defaultMessage: 'Разложить остаток по массивам не вышло: ни одна работа на поле среза не сопоставилась с контуром, у которого есть массив. Это пробел связи, а не отсутствие отставания.',
    description: 'Отклонение в ответе об отстающих массивах, когда разложить по массивам не удалось',
  },
  lagDevWorst: { id: 'explore.lag.dev.worst', defaultMessage: 'Ниже района держатся <list></list>.', description: 'Отклонение в ответе об отстающих массивах; <list> — перечисление трёх массивов (каждый — отдельная фраза «…»), точка закрывает предложение' },
  lagDevWorstItem: {
    id: 'explore.lag.dev.worstItem',
    defaultMessage: '<link>{name}</link> — {share} из {total, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работы на полях}} ({ha} га работ)',
    description: 'Элемент перечисления массивов-отстающих; <link> — ссылка на массив, {share} — процент, {total} — работ на полях, {ha} — сумма гектаров этих работ',
  },
  lagDevBest: {
    id: 'explore.lag.dev.best',
    defaultMessage: 'Верхняя точка среза — <link>{name}</link> с {share} из {total, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работы на полях}}.',
    description: 'Отклонение в ответе об отстающих массивах: массив с самой высокой долей; <link> — ссылка на массив',
  },
  lagDevRest: {
    id: 'explore.lag.dev.rest',
    defaultMessage:
      'Остаток по району — {rest} без отчёта, из них {silent} по уже закрытым окнам; они распределены по {farms, plural, one {# хозяйству} few {# хозяйствам} many {# хозяйствам} other {# хозяйствам}}. Это утверждение о данных, а не о поле: отчёт мог не дойти, а работа быть сделанной.',
    description: 'Отклонение в ответе об отстающих массивах: остаток без отчёта по району; {rest} — «120 работ на полях», {silent} — число (строка), {farms} — число хозяйств',
  },
  lagCauseLate: {
    id: 'explore.lag.cause.late',
    defaultMessage: 'Поздние отчёты: {late} из {reported} поступивших ({share}) пришли после закрытия своего окна. Канал отчётности работает, но с задержкой — это факт о датах записей.',
    description: 'Причина в ответе об отстающих массивах: поздние отчёты; {late}/{reported} — числа, {share} — процент',
  },
  lagCauseSilent: {
    id: 'explore.lag.cause.silent',
    defaultMessage: 'Молчание: {silent} по закрытым окнам не получили ни одной записи. Молчание отличается от опоздания — по нему нет ни даты, ни автора, поэтому причину срез назвать не может.',
    description: 'Причина в ответе об отстающих массивах: молчание; {silent} — «50 работ на полях»',
  },
  lagCauseDenominator: {
    id: 'explore.lag.cause.denominator',
    defaultMessage: 'Знаменатель: {upcoming} относятся к работам впереди и в счёт не идут — у них «ещё не срок», а не «не сделано». Если бы они попали в долю, отставание выглядело бы больше, чем оно есть.',
    description: 'Причина в ответе об отстающих массивах: что не вошло в знаменатель; {upcoming} — «30 работ на полях»',
  },
  lagMethod: { id: 'explore.lag.method', defaultMessage: 'группировка работ на полях по массиву через контур, доля отчитавшихся от работ на полях открытых окон, сортировка по возрастанию доли', description: 'Строка «Как посчитано» под ответом об отстающих массивах (строчными)' },

  /* --- «Где вода недопоставлена» --- */
  waterTitle: { id: 'explore.water.title', defaultMessage: 'Где вода недопоставлена', description: 'Заголовок ответа аналитики о недопоставке воды' },
  waterOpenWork: { id: 'explore.water.openWork', defaultMessage: 'Открыть работу «{name}»', description: 'Кнопка-ссылка в «Рекомендуемых действиях»: перейти к поливной работе; {name} — название работы' },
  waterOpenWorkHint: { id: 'explore.water.openWorkHint', defaultMessage: 'самый крупный полив агроцикла: окно, две оси и разбивка по хозяйствам', description: 'Подсказка под кнопкой «Открыть работу» в ответе о воде (строчными)' },
  waterOpenCycleHint: { id: 'explore.water.openCycleHint', defaultMessage: 'поливных работ в срезе не нашлось — вода видна на уровне агроцикла', description: 'Подсказка под кнопкой «Открыть агроцикл» в ответе о воде (строчными), когда поливных работ нет' },
  waterOpenMassif: { id: 'explore.water.openMassif', defaultMessage: 'Открыть массив {name}', description: 'Кнопка-ссылка в «Рекомендуемых действиях»: перейти к массиву; {name} — название массива' },
  waterOpenMassifAny: { id: 'explore.water.openMassifAny', defaultMessage: 'Открыть массив с наименьшим покрытием', description: 'Кнопка-ссылка в «Рекомендуемых действиях», когда массива нет: открыть массив с наименьшим покрытием воды' },
  waterOpenMassifHint: { id: 'explore.water.openMassifHint', defaultMessage: 'контуры, хозяйства и работы массива с наименьшим покрытием заявок', description: 'Подсказка под кнопкой «Открыть массив» в ответе о воде (строчными)' },
  waterAssign: { id: 'explore.water.assign', defaultMessage: 'Создать поручение по водоподаче', description: 'Кнопка-ссылка в «Рекомендуемых действиях» ответа о воде: поручение по подаче воды' },
  waterSubjectMassif: { id: 'explore.water.subjectMassif', defaultMessage: 'водоподача, массив {name}, агроцикл «{cycle}»', description: 'Предмет поручения словами (строчными): подача воды, массив и агроцикл' },
  waterSubjectCycle: { id: 'explore.water.subjectCycle', defaultMessage: 'водоподача по агроциклу «{cycle}»', description: 'Предмет поручения словами (строчными): подача воды по агроциклу' },
  waterSummary: {
    id: 'explore.water.summary',
    defaultMessage:
      'По агроциклу «{cycle}» запрошено {requested}, подано {delivered} — покрытие {coverage} по {requests, plural, one {# заявке} few {# заявкам} many {# заявкам} other {# заявкам}}, разрыв {gap}. Четыре числа взяты из сводки воды как есть, без пересчёта.',
    description: 'Краткий вывод ответа «Где вода недопоставлена»; {requested}/{delivered}/{gap} — объёмы в м³, {coverage} — процент, {requests} — число заявок',
  },
  waterDevWorst: { id: 'explore.water.dev.worst', defaultMessage: 'Ниже всех покрытие в массивах <list></list>.', description: 'Отклонение в ответе о воде; <list> — перечисление трёх массивов с наименьшим покрытием' },
  waterDevWorstItem: {
    id: 'explore.water.dev.worstItem',
    defaultMessage: '<link>{name}</link> — {coverage} ({delivered} из {requested}, {n, plural, one {# заявка} few {# заявки} many {# заявок} other {# заявки}})',
    description: 'Элемент перечисления массивов с низким покрытием воды; <link> — ссылка на массив, {coverage} — процент, {delivered}/{requested} — объёмы в м³, {n} — число заявок',
  },
  waterDevStatus: {
    id: 'explore.water.dev.status',
    defaultMessage:
      'Из {total, plural, one {# заявки} few {# заявок} many {# заявок} other {# заявки}} {partial} закрыты частично и {pending} ещё ждут подачи. «В ожидании» — состояние заявки, а не отказ: часть разрыва может закрыться внутри своего окна.',
    description: 'Отклонение в ответе о воде: состояния заявок; {total} — всего заявок, {partial}/{pending} — числа (строки)',
  },
  waterDevEmpty: { id: 'explore.water.dev.empty', defaultMessage: 'заявок на воду в срезе агроцикла нет ни одной — разбирать нечего: это отсутствие записей, а не нулевая подача', description: 'Что печатать вместо отклонений в ответе о воде (строчными), когда заявок нет' },
  waterCauseUneven: {
    id: 'explore.water.cause.uneven',
    defaultMessage: 'Недопоставка распределена неравномерно: между верхним и нижним массивом разница покрытия — {gap}. Разрыв идёт не от общего дефицита, а от того, куда попала подача.',
    description: 'Причина в ответе о воде; {gap} — разница покрытия в п.п.',
  },
  waterCauseAssumption: {
    id: 'explore.water.cause.assumption',
    defaultMessage:
      'Запрошенный объём — сам по себе производная величина: для поливных работ без нормы в шаблоне генератор принял норму по культуре и записал это допущение в метаданные среза. Разрыв поэтому говорит о соотношении заявки и подачи, а не о дефиците воды в реке.',
    description: 'Причина в ответе о воде (класс «допущение прототипа»): запрошенный объём получен расчётом',
  },
  waterCauseNoData: {
    id: 'explore.water.cause.noData',
    defaultMessage:
      'Заявка в срезе привязана к паре «хозяйство × работа»: каналов, насосных станций и качества воды в данных нет вовсе. Причину «почему не дошло» этими данными не установить — её в них не записано.',
    description: 'Причина в ответе о воде: чего нет в данных',
  },
  waterMethod: { id: 'explore.water.method', defaultMessage: 'суммы запрошенного и поданного по массиву из строк заявок, покрытие = подано ÷ запрошено, сортировка по возрастанию покрытия', description: 'Строка «Как посчитано» под ответом о воде (строчными)' },

  /* --- «Какие окна закрываются на неделе» --- */
  winTitle: { id: 'explore.windows.title', defaultMessage: 'Какие окна закрываются на неделе', description: 'Заголовок ответа аналитики об окнах работ, закрывающихся в ближайшую неделю' },
  winClosing: {
    id: 'explore.windows.closing',
    defaultMessage: 'Закрываются: <list></list>{hasMore, select, yes { и ещё {more}} other {}}.',
    description: 'Отклонение в ответе об окнах: закрывающиеся окна; <list> — перечисление работ, {hasMore} — yes/no, {more} — сколько ещё не показано',
  },
  winClosingItem: {
    id: 'explore.windows.closingItem',
    defaultMessage:
      '<link>{name}</link> — {date}, осталось {days, plural, one {# день} few {# дня} many {# дней} other {# дня}}, отчитались {reported} из {total, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работы на полях}} ({share}), {ha} га работ',
    description: 'Элемент перечисления закрывающихся окон; <link> — ссылка на работу, {date} — дата закрытия, {days} — осталось дней, {reported} — число отчитавшихся, {total} — работ на полях, {share} — процент, {ha} — гектары работ',
  },
  winOpening: {
    id: 'explore.windows.opening',
    defaultMessage: 'Открывается <list></list>. Охват и запуск — на <link>экране кампаний</link>.',
    description: 'Отклонение в ответе об окнах: открывающиеся окна; <list> — перечисление работ, <link> — ссылка на экран кампаний',
  },
  winOpeningItem: {
    id: 'explore.windows.openingItem',
    defaultMessage: '<link>{name}</link> — {date}, {works}, {ha} га работ',
    description: 'Элемент перечисления открывающихся окон; <link> — ссылка на работу, {date} — дата открытия, {works} — «120 работ на полях», {ha} — гектары работ',
  },
  winSummaryNone: {
    id: 'explore.windows.summaryNone',
    defaultMessage:
      'За семь дней от {date} в агроцикле «{cycle}» не закрывается и не открывается ни одно окно. Это полный ноль по названному отрезку, а не отсутствие данных: открытых и предстоящих работ в агроцикле {count}.',
    description: 'Краткий вывод ответа об окнах, когда окон на неделе нет; {date} — дата среза, {cycle} — «Хлопок 2026», {count} — число работ (строка)',
  },
  winSummary: {
    id: 'explore.windows.summary',
    defaultMessage:
      'За семь дней от {date} в агроцикле «{cycle}» закрывается {closing} и открывается {opening}; всего открытых и предстоящих работ в агроцикле {total}.',
    description: 'Краткий вывод ответа об окнах; {closing}/{opening} — «3 работы», {total} — число работ (строка)',
  },
  winDevEmpty: { id: 'explore.windows.devEmpty', defaultMessage: 'в семидневном горизонте от {date} нет ни закрывающихся, ни открывающихся окон — ближайшее движение позже этой недели', description: 'Что печатать вместо отклонений в ответе об окнах (строчными); {date} — дата среза' },
  winCauseNotLag: {
    id: 'explore.windows.cause.notLag',
    defaultMessage: 'Остаток «без отчёта» в идущем окне — это не отставание: срок ещё не вышел, и отчёты по таким работам приходят к концу окна. Отставанием он станет только после даты закрытия.',
    description: 'Причина в ответе об окнах: остаток в идущем окне — не отставание',
  },
  winCausePast: {
    id: 'explore.windows.cause.past',
    defaultMessage: 'Прошлые окна оставили {silent} без отчёта — этот остаток на неделю уже не влияет, но он есть, и закрывается он вводом за хозяйство, а не сроком.',
    description: 'Причина в ответе об окнах; {silent} — «50 работ на полях»',
  },
  winCauseHorizon: {
    id: 'explore.windows.cause.horizon',
    defaultMessage: 'Горизонт задан рецептом: семь дней от даты среза {date}. Работы за его пределами не «в порядке» — они просто не попали в вопрос.',
    description: 'Причина в ответе об окнах: что такое горизонт; {date} — полная дата среза',
  },
  winMethod: { id: 'explore.windows.method', defaultMessage: 'окна работ активного агроцикла, у которых конец или начало попадают в семь дней от даты среза; счётчики взяты из итогов работы', description: 'Строка «Как посчитано» под ответом об окнах (строчными)' },
  winOpenWorkHint: { id: 'explore.windows.openWorkHint', defaultMessage: 'ближайшее окно: две оси, карта работы и список отчётов с авторством', description: 'Подсказка под кнопкой «Открыть работу» в ответе об окнах (строчными)' },
  winOpenCycleHint: { id: 'explore.windows.openCycleHint', defaultMessage: 'хронология окон агроцикла целиком', description: 'Подсказка под кнопкой «Открыть агроцикл» в ответе об окнах (строчными)' },
  winCampaign: { id: 'explore.windows.campaign', defaultMessage: 'Запустить кампанию по предстоящему окну', description: 'Кнопка-ссылка в «Рекомендуемых действиях» ответа об окнах: перейти к экрану кампаний' },
  winCampaignHint: { id: 'explore.windows.campaignHint', defaultMessage: 'охват культуры и контуров; запуск в прототипе живёт до конца сессии и данные не меняет', description: 'Подсказка под кнопкой «Запустить кампанию» (строчными)' },
  winAssign: { id: 'explore.windows.assign', defaultMessage: 'Создать поручение со сроком', description: 'Кнопка-ссылка в «Рекомендуемых действиях» ответа об окнах: поручение со сроком' },
  winSubjectWork: { id: 'explore.windows.subjectWork', defaultMessage: 'работа «{name}», окно до {date}', description: 'Предмет поручения словами (строчными): работа и дата конца её окна' },
  winOpenWork: { id: 'explore.windows.openWork', defaultMessage: 'Открыть работу «{name}»', description: 'Кнопка-ссылка в «Рекомендуемых действиях»: перейти к работе; {name} — название работы' },

  /* --- «Что с карантином» --- */
  quTitle: { id: 'explore.quarantine.title', defaultMessage: 'Что с карантином', description: 'Заголовок ответа аналитики о карантине' },
  quSummaryNoInspections: {
    id: 'explore.quarantine.summary.noInspections',
    defaultMessage: 'В срезе зелёных контуров — {green}, красных и жёлтых — {watch}. Обследований и покрытия источник не называет: {note}.',
    description: 'Краткий вывод ответа о карантине, когда журнала обследований нет; {green}/{watch} — числа контуров, {note} — пояснение источника',
  },
  quNoJournal: { id: 'explore.quarantine.noJournal', defaultMessage: 'районного журнала нет', description: 'Пояснение по умолчанию, почему нет обследований (строчными): районного журнала нет' },
  quSummaryInspections: {
    id: 'explore.quarantine.summary.inspections',
    defaultMessage:
      'В срезе зелёных контуров — {green}, красных и жёлтых — {watch}, при {inspections, plural, one {# обследовании} few {# обследованиях} many {# обследованиях} other {# обследованиях}} и покрытии {coverage}.',
    description: 'Краткий вывод ответа о карантине; {green}/{watch} — числа контуров, {inspections} — число обследований, {coverage} — процент',
  },
  quSummaryFindings: {
    id: 'explore.quarantine.summary.findings',
    defaultMessage:
      'Из {count, plural, one {# обследования} few {# обследований} many {# обследований} other {# обследований}} с находкой чаще других встречаются {names}.',
    description: 'Краткий вывод ответа о карантине: самые частые находки; {count} — число обследований с находкой, {names} — «Название (3), Название (2)»',
  },
  quSummaryNoFindings: { id: 'explore.quarantine.summary.noFindings', defaultMessage: 'Обследований с находкой в журнале нет ни одного.', description: 'Краткий вывод ответа о карантине, когда находок нет' },
  quDevCoverage: {
    id: 'explore.quarantine.dev.coverage',
    defaultMessage:
      'Обследования есть по {inspected, plural, one {# контуру} few {# контурам} many {# контурам} other {# контурам}} из {total, plural, one {# контура} few {# контуров} many {# контуров} other {# контура}} агроцикла — по остальным {uncovered} в срезе нет ни одной записи. У них не «чисто», а «неизвестно»: молчание журнала ничего не утверждает о поле.',
    description: 'Отклонение в ответе о карантине: покрытие обследованиями; {inspected} — обследованных контуров, {total} — контуров агроцикла, {uncovered} — необследованных (число, строка)',
  },
  quDevWatch: {
    id: 'explore.quarantine.dev.watch',
    defaultMessage:
      'Красных и жёлтых: {watch}{hasFreshest, select, yes {, самый свежий очаг открыт {date} — <link>карточка контура</link>{hasCause, select, yes {, причина в журнале: {cause}} other {}}} other {}}.',
    description: 'Отклонение в ответе о карантине: красные и жёлтые контуры; {watch} — «20 контуров», {hasFreshest}/{hasCause} — yes/no, {date} — дата открытия очага, <link> — ссылка на контур, {cause} — причина из журнала',
  },
  quDevKinds: {
    id: 'explore.quarantine.dev.kinds',
    defaultMessage: 'Находки по видам: {kinds}. Классификации A1/A2/A3 в срезе нет: вид находки — свободный текст журнала, а не позиция справочника.',
    description: 'Отклонение в ответе о карантине: находки по видам; {kinds} — «вид — число, вид — число»',
  },
  quDevKindsNone: {
    id: 'explore.quarantine.dev.kindsNone',
    defaultMessage: 'Находок в журнале нет: все записи — «без находок». Классификации A1/A2/A3 в срезе тоже нет, вид находки хранился бы свободным текстом.',
    description: 'Отклонение в ответе о карантине, когда находок нет',
  },
  quCauseCoverage: {
    id: 'explore.quarantine.cause.coverage',
    defaultMessage: 'Покрытие неполное: {coverage} обследованных значит, что картина собрана по части контуров. Рост числа очагов на таких данных может быть и ростом внимания, а не ростом заражения.',
    description: 'Причина в ответе о карантине; {coverage} — процент обследованных контуров',
  },
  quCauseWatch: {
    id: 'explore.quarantine.cause.watch',
    defaultMessage:
      '«Наблюдение» — статус обследования, а не приговор контуру: красной шкалы в системе нет, и повышать статус до тревоги экран не станет. Предписаний об устранении, повторных измерений и передачи в агроинспекцию в срезе тоже нет — из шести шагов канона в данных живут два.',
    description: 'Причина в ответе о карантине: что означает статус «наблюдение» и чего нет в данных',
  },
  quMethod: { id: 'explore.quarantine.method', defaultMessage: 'подсчёт находок по видам и названиям из журнала обследований, покрытие = число обследованных контуров ÷ контуры агроцикла', description: 'Строка «Как посчитано» под ответом о карантине (строчными)' },
  quActionCase: { id: 'explore.quarantine.action.case', defaultMessage: 'Разобрать контур свежего очага', description: 'Кнопка-ссылка в «Рекомендуемых действиях» ответа о карантине: открыть разбор контура' },
  quActionCaseHint: { id: 'explore.quarantine.action.caseHint', defaultMessage: 'кейс контура: паспорт, история культур, работы и наблюдения в этой же форме', description: 'Подсказка под кнопкой «Разобрать контур свежего очага» (строчными)' },
  quActionRegistry: { id: 'explore.quarantine.action.registry', defaultMessage: 'Открыть реестр контуров', description: 'Кнопка-ссылка в «Рекомендуемых действиях» ответа о карантине, когда контуров под наблюдением нет' },
  quActionRegistryHint: { id: 'explore.quarantine.action.registryHint', defaultMessage: 'контуров под наблюдением в срезе нет — реестр показывает картину целиком', description: 'Подсказка под кнопкой «Открыть реестр контуров» (строчными)' },
  quActionMap: { id: 'explore.quarantine.action.map', defaultMessage: 'Открыть карту со слоем карантина', description: 'Кнопка-ссылка в «Рекомендуемых действиях» ответа о карантине: карта с карантином' },
  quActionMapHint: { id: 'explore.quarantine.action.mapHint', defaultMessage: 'статус контура читается обводкой и подписью, а не заливкой', description: 'Подсказка под кнопкой «Открыть карту со слоем карантина» (строчными)' },
  quActionAssign: { id: 'explore.quarantine.action.assign', defaultMessage: 'Создать поручение на повторное обследование', description: 'Кнопка-ссылка в «Рекомендуемых действиях» ответа о карантине: поручение на повторное обследование' },
  quSubjectContour: { id: 'explore.quarantine.subjectContour', defaultMessage: 'контур {no}, очаг под наблюдением', description: 'Предмет поручения словами (строчными): контур и очаг; {no} — номер контура' },
  quSubjectAny: { id: 'explore.quarantine.subjectAny', defaultMessage: 'повторное обследование по контурам под наблюдением', description: 'Предмет поручения словами (строчными), когда конкретного контура нет' },

  /* --- «Сравни дисциплину массивов» --- */
  cmpTitleNone: { id: 'explore.compare.titleNone', defaultMessage: 'Сравнение массивов', description: 'Заголовок ответа аналитики о сравнении массивов, когда сравнить нечего' },
  cmpSummaryNone: {
    id: 'explore.compare.summaryNone',
    defaultMessage:
      'Сравнивать нечего: в срезе меньше двух массивов, где хотя бы {count, plural, one {# работа на поле} few {# работы на полях} many {# работ на полях} other {# работы на полях}} в открытых окнах. Сравнение на маленьких знаменателях говорит о случайности, а не о дисциплине.',
    description: 'Краткий вывод ответа о сравнении массивов, когда пары нет; {count} — порог числа работ на полях',
  },
  cmpDevEmpty: { id: 'explore.compare.devEmpty', defaultMessage: 'пары для сравнения не собралось — отклонения не считаются на знаменателе, который сам ненадёжен', description: 'Что печатать вместо отклонений в ответе о сравнении (строчными), когда пары нет' },
  cmpCauseFloor: {
    id: 'explore.compare.cause.floor',
    defaultMessage:
      'Порог {floor} задан рецептом намеренно: на меньшем знаменателе один невернувшийся отчёт двигает долю на проценты, и разница между массивами перестаёт что-либо значить.',
    description: 'Причина в ответе о сравнении, когда пары нет; {floor} — порог, «100 работ на полях»',
  },
  cmpMethodNone: { id: 'explore.compare.methodNone', defaultMessage: 'отбор массивов с достаточным знаменателем; при нехватке сравнение не строится', description: 'Строка «Как посчитано» под ответом о сравнении, когда пары нет (строчными)' },
  cmpActionPickHint: { id: 'explore.compare.action.pickHint', defaultMessage: 'пару выбираете вы: список «лучших» и «худших» экран сравнения не показывает', description: 'Подсказка под кнопкой «Сравнить два хозяйства» в ответе о сравнении, когда пары нет (строчными)' },
  cmpTitle: { id: 'explore.compare.title', defaultMessage: 'Дисциплина массивов: {low} и {high}', description: 'Заголовок ответа аналитики о сравнении двух массивов; {low}/{high} — названия массивов' },
  cmpSummary: {
    id: 'explore.compare.summary',
    defaultMessage:
      'Ось сравнения одна — доля работ на полях с отчётом на срезе {date}, агроцикл «{cycle}». <lowLink>{low}</lowLink> — {lowShare} из {lowTotal, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работы на полях}}, <highLink>{high}</highLink> — {highShare} из {highTotal, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работы на полях}}; разрыв {gap}.',
    description: 'Краткий вывод ответа о сравнении двух массивов; <lowLink>/<highLink> — ссылки на массивы, {lowShare}/{highShare} — проценты, {lowTotal}/{highTotal} — числа работ на полях, {gap} — разрыв в п.п., {date} — полная дата, {cycle} — «Хлопок 2026»',
  },
  cmpDevLow: {
    id: 'explore.compare.dev.low',
    defaultMessage:
      '<link>{name}</link> — {share} из {total, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работы на полях}} ({ha} га работ), без отчёта по закрытым окнам {silent}, с опозданием {late}.',
    description: 'Отклонение в ответе о сравнении: массив с нижней долей; <link> — ссылка на массив, {share} — процент, {total} — работ на полях, {ha} — гектары работ, {silent}/{late} — числа',
  },
  cmpDevHigh: {
    id: 'explore.compare.dev.high',
    defaultMessage:
      '<link>{name}</link> — {share} из {total, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работы на полях}} ({ha} га работ), без отчёта {silent}, с опозданием {late}.',
    description: 'Отклонение в ответе о сравнении: массив с верхней долей; <link> — ссылка на массив, {share} — процент, {total} — работ на полях, {ha} — гектары работ, {silent}/{late} — числа',
  },
  cmpCauseRatio: {
    id: 'explore.compare.cause.ratio',
    defaultMessage: 'Знаменатели отличаются в {ratio} раза, поэтому сравнивать можно доли, но не абсолютные числа: у меньшего массива один невернувшийся отчёт двигает долю заметно сильнее.',
    description: 'Причина в ответе о сравнении; {ratio} — во сколько раз различаются знаменатели (число с одним знаком)',
  },
  cmpCauseSplit: {
    id: 'explore.compare.cause.split',
    defaultMessage:
      'Разрыв складывается из двух разных вещей: молчания ({lowSilent} против {highSilent}) и опозданий ({lowLate} против {highLate}). Первое — это отсутствие записи, второе — запись не в срок; лечатся они по-разному.',
    description: 'Причина в ответе о сравнении: из чего складывается разрыв; все значения — числа',
  },
  cmpCauseNoScore: {
    id: 'explore.compare.cause.noScore',
    defaultMessage: 'Сводного балла у пары нет и не будет: сравниваются факты в общем знаменателе, а оценку выносит специалист. Рейтинга массивов и хозяйств прототип не строит принципиально.',
    description: 'Причина в ответе о сравнении: почему нет оценки и рейтинга',
  },
  cmpMethod: { id: 'explore.compare.method', defaultMessage: 'две крайние точки среди массивов с ≥ {floor} работ на полях: минимальная и максимальная доля отчитавшихся', description: 'Строка «Как посчитано» под ответом о сравнении (строчными); {floor} — порог, число' },
  cmpOpenLowHint: { id: 'explore.compare.openLowHint', defaultMessage: 'контуры, хозяйства и работы массива с нижней долей', description: 'Подсказка под кнопкой «Открыть массив» (нижняя доля), строчными' },
  cmpOpenHighHint: { id: 'explore.compare.openHighHint', defaultMessage: 'та же картина у верхней точки — сравнение без общего знаменателя не читается', description: 'Подсказка под кнопкой «Открыть массив» (верхняя доля), строчными' },
  cmpSideBySide: { id: 'explore.compare.sideBySide', defaultMessage: 'Сравнить два хозяйства бок о бок', description: 'Кнопка-ссылка в «Рекомендуемых действиях» ответа о сравнении: сравнение хозяйств' },

  /* --- «Кто из хозяйств молчит по закрытым окнам» --- */
  siTitle: { id: 'explore.silent.title', defaultMessage: 'Кто из хозяйств молчит по закрытым окнам', description: 'Заголовок ответа аналитики о хозяйствах без отчётов по закрытым окнам' },
  siSummaryNone: {
    id: 'explore.silent.summaryNone',
    defaultMessage:
      'По закрытым окнам агроцикла «{cycle}» без отчёта не осталось ни одной работы на поле: все закрытые окна получили запись. Это утверждение о данных — о том, что записи дошли, а не о том, что работы выполнены.',
    description: 'Краткий вывод ответа о молчащих хозяйствах, когда молчания нет; {cycle} — «Хлопок 2026»',
  },
  siSummary: {
    id: 'explore.silent.summary',
    defaultMessage:
      'По закрытым окнам агроцикла «{cycle}» осталось без отчёта {silent}; они распределены по {rows, plural, one {# хозяйству} few {# хозяйствам} many {# хозяйствам} other {# хозяйствам}} из {total, plural, one {# хозяйства} few {# хозяйств} many {# хозяйств} other {# хозяйства}}, участвующих в агроцикле. Это список молчания, а не список нарушителей: «без отчёта» говорит, что запись не дошла.',
    description: 'Краткий вывод ответа о молчащих хозяйствах; {silent} — «50 работ на полях», {rows} — сколько хозяйств молчит, {total} — сколько хозяйств в агроцикле',
  },
  siDevTop: {
    id: 'explore.silent.dev.top',
    defaultMessage: 'Больше других работ на полях без отчёта у <list></list>{hasMore, select, yes {; всего таких хозяйств {total}} other {}}.',
    description: 'Отклонение в ответе о молчащих хозяйствах; <list> — перечисление хозяйств, {hasMore} — yes/no, {total} — сколько всего таких хозяйств',
  },
  siDevTopItem: {
    id: 'explore.silent.dev.topItem',
    defaultMessage: '<link>{name}</link> — {works}, {ha} га работ',
    description: 'Элемент перечисления молчащих хозяйств; <link> — ссылка на хозяйство, {works} — «12 работ на полях», {ha} — сумма гектаров этих работ',
  },
  siDevOldest: {
    id: 'explore.silent.dev.oldest',
    defaultMessage: 'Самое раннее закрытое окно без отчёта — {date}: молчание в срезе накопленное, а не сегодняшнее. Хозяйств без единой молчащей работы на поле — {count}.',
    description: 'Отклонение в ответе о молчащих хозяйствах; {date} — дата закрытия самого раннего окна или «—», {count} — число хозяйств',
  },
  siDevEmpty: { id: 'explore.silent.devEmpty', defaultMessage: 'молчащих работ на полях по закрытым окнам в срезе нет — все закрытые окна получили запись', description: 'Что печатать вместо отклонений в ответе о молчащих хозяйствах (строчными)' },
  siCauseWindow: {
    id: 'explore.silent.cause.window',
    defaultMessage: 'Окно закрылось раньше, чем пришла запись. Срез хранит дату отчёта и автора; там, где записи нет, нет и причины — её называет человек при вводе за хозяйство, а не рецепт.',
    description: 'Причина в ответе о молчащих хозяйствах: окно закрылось раньше записи',
  },
  siCauseLate: {
    id: 'explore.silent.cause.late',
    defaultMessage:
      'Рядом с молчанием живёт опоздание: {late, plural, one {# отчёт по агроциклу пришёл} few {# отчёта по агроциклу пришли} many {# отчётов по агроциклу пришли} other {# отчёта по агроциклу пришли}} после закрытия окна. Это разные состояния канала — во втором случае связь есть, но с задержкой.',
    description: 'Причина в ответе о молчащих хозяйствах: опоздавшие отчёты; {late} — число поздних отчётов',
  },
  siCauseNotDone: {
    id: 'explore.silent.cause.notDone',
    defaultMessage: '«Без отчёта» ≠ «не выполнено»: работа могла быть сделана и не попасть в систему. Поэтому следующий шаг — уточнить и внести запись с указанием причины, а не менять статус работы.',
    description: 'Причина в ответе о молчащих хозяйствах: «без отчёта» не значит «не выполнено»',
  },
  siMethod: { id: 'explore.silent.method', defaultMessage: 'работы на полях в состоянии «без отчёта» (окно закрыто, отчёта нет), группировка по хозяйству, сортировка по числу работ', description: 'Строка «Как посчитано» под ответом о молчащих хозяйствах (строчными)' },
  siActionEntry: { id: 'explore.silent.action.entry', defaultMessage: 'Внести отчёт за хозяйство', description: 'Кнопка-ссылка в «Рекомендуемых действиях» ответа о молчащих хозяйствах: ввод отчёта менеджером за хозяйство' },
  siActionEntryHint: { id: 'explore.silent.action.entryHint', defaultMessage: 'источник «менеджер за хозяйство» и причина обязательны — запись не выдаёт себя за отчёт самого хозяйства', description: 'Подсказка под кнопкой «Внести отчёт за хозяйство» (строчными)' },
  siActionAssign: { id: 'explore.silent.action.assign', defaultMessage: 'Создать поручение — уточнить статус работ', description: 'Кнопка-ссылка в «Рекомендуемых действиях» ответа о молчащих хозяйствах: поручение уточнить статус работ' },
  siSubjectFarm: { id: 'explore.silent.subjectFarm', defaultMessage: 'хозяйство {name}, агроцикл «{cycle}»', description: 'Предмет поручения словами (строчными): хозяйство и агроцикл' },

  /* --- готовые вопросы --- */
  presetLagQ: { id: 'explore.presets.lag.question', defaultMessage: 'Какие массивы отстают по отчётам?', description: 'Готовый вопрос на экране «ИИ-аналитика» (кнопка-карточка): отстающие массивы' },
  presetLagWhy: { id: 'explore.presets.lag.why', defaultMessage: 'уровень массива, знаменатель — работы на полях открытых окон активного агроцикла', description: 'Пояснение под готовым вопросом (мелко, строчными): на каком уровне считается и какой знаменатель' },
  presetWaterQ: { id: 'explore.presets.water.question', defaultMessage: 'Где вода недопоставлена?', description: 'Готовый вопрос на экране «ИИ-аналитика»: недопоставка воды' },
  presetWaterWhy: { id: 'explore.presets.water.why', defaultMessage: 'уровень массива, знаменатель — заявки поливных работ агроцикла', description: 'Пояснение под готовым вопросом (мелко, строчными)' },
  presetWindowsQ: { id: 'explore.presets.windows.question', defaultMessage: 'Какие окна закрываются на неделе?', description: 'Готовый вопрос на экране «ИИ-аналитика»: окна, закрывающиеся на неделе' },
  presetWindowsWhy: { id: 'explore.presets.windows.why', defaultMessage: 'уровень района, горизонт — семь дней от даты среза', description: 'Пояснение под готовым вопросом (мелко, строчными)' },
  presetQuarantineQ: { id: 'explore.presets.quarantine.question', defaultMessage: 'Что с карантином?', description: 'Готовый вопрос на экране «ИИ-аналитика»: карантин' },
  presetQuarantineWhy: { id: 'explore.presets.quarantine.why', defaultMessage: 'уровень контура, знаменатель — контуры агроцикла и журнал обследований', description: 'Пояснение под готовым вопросом (мелко, строчными)' },
  presetCompareQ: { id: 'explore.presets.compare.question', defaultMessage: 'Сравни дисциплину массивов {a} и {b}', description: 'Готовый вопрос на экране «ИИ-аналитика»: сравнение двух массивов; {a}/{b} — названия массивов' },
  presetCompareQAny: { id: 'explore.presets.compare.questionAny', defaultMessage: 'Сравни дисциплину двух массивов', description: 'Готовый вопрос на экране «ИИ-аналитика»: сравнение двух массивов, когда конкретные не определены' },
  presetCompareWhy: { id: 'explore.presets.compare.why', defaultMessage: 'сравнение по одной оси с общим знаменателем и оговоркой о размере', description: 'Пояснение под готовым вопросом (мелко, строчными)' },
  presetSilentQ: { id: 'explore.presets.silent.question', defaultMessage: 'Кто из хозяйств молчит по закрытым окнам?', description: 'Готовый вопрос на экране «ИИ-аналитика»: молчащие хозяйства' },
  presetSilentWhy: { id: 'explore.presets.silent.why', defaultMessage: 'уровень хозяйства; «без отчёта» — про данные, а не про работу в поле', description: 'Пояснение под готовым вопросом (мелко, строчными)' },
  presetCaseQ: { id: 'explore.presets.case.question', defaultMessage: 'Разбор контура по номеру', description: 'Готовая кнопка на экране «ИИ-аналитика»: разбор одного контура по номеру' },
  presetCaseWhy: { id: 'explore.presets.case.why', defaultMessage: 'уровень контура: паспорт, история культур, работы обоих агроциклов и наблюдения', description: 'Пояснение под кнопкой «Разбор контура по номеру» (мелко, строчными)' },

  /* --- экран --- */
  districtName: { id: 'explore.page.districtName', defaultMessage: 'Бука', description: 'Название района (Бука) в хлебных крошках по умолчанию; географическое имя — транслитерация' },
  trailDistrict: { id: 'explore.page.trail.district', defaultMessage: 'Район', description: 'Название уровня в хлебных крошках: район' },
  trailSection: { id: 'explore.page.trail.section', defaultMessage: 'Раздел', description: 'Название уровня в хлебных крошках: раздел приложения' },
  trailSectionName: { id: 'explore.page.trail.sectionName', defaultMessage: 'Разбор данных', description: 'Название раздела в хлебных крошках: «Разбор данных» (экран ИИ-аналитики)' },
  trailContour: { id: 'explore.page.trail.contour', defaultMessage: 'Контур', description: 'Название уровня в хлебных крошках: контур' },
  trailCase: { id: 'explore.page.trail.case', defaultMessage: 'разбор', description: 'Название страницы в хлебных крошках внутри уровня «Контур»; строчными' },
  pageTitle: { id: 'explore.page.title', defaultMessage: 'ИИ-аналитика', description: 'Заголовок экрана «ИИ-аналитика» (менеджер, аналитик)' },
  pagePurpose: {
    id: 'explore.page.purpose',
    defaultMessage:
      'Форма ответа из презентации — краткий вывод, отклонения, причины, связанные данные, рекомендуемые действия — на реальных числах среза {date}. Готовые вопросы считаются по фиксированным рецептам из данных среза; языковой модели за экраном нет.',
    description: 'Подзаголовок экрана «ИИ-аналитика»; {date} — полная дата среза («26 августа 2026»)',
  },
  pageMark: { id: 'explore.page.mark', defaultMessage: 'модель не подключена', description: 'Метка доверия справа в заголовке экрана «ИИ-аналитика»: языковая модель не подключена; строчными' },
  askPlaceholder: { id: 'explore.page.ask.placeholder', defaultMessage: 'Спросите о районе — или выберите готовый вопрос ниже', description: 'Подсказка в поле ввода вопроса на экране «ИИ-аналитика»' },
  askAria: { id: 'explore.page.ask.aria', defaultMessage: 'Запрос к аналитике района', description: 'aria-label поля ввода вопроса на экране «ИИ-аналитика»' },
  askButton: { id: 'explore.page.ask.button', defaultMessage: 'Спросить', description: 'Кнопка отправки вопроса на экране «ИИ-аналитика»; коротко' },
  freeTitle: { id: 'explore.page.free.title', defaultMessage: 'Свободные вопросы появятся вместе с моделью', description: 'Заголовок сообщения о том, что свободный вопрос пока не разбирается' },
  freeText: {
    id: 'explore.page.free.text',
    defaultMessage:
      'Вопрос «{text}» прототип разобрать не может: за строкой нет ни модели, ни сервера, а придуманный ответ хуже честного отказа. Ближайший готовый вопрос — «{question}».',
    description: 'Пояснение под заголовком «Свободные вопросы появятся вместе с моделью»; {text} — введённый вопрос, {question} — ближайший готовый вопрос',
  },
  freeAsk: { id: 'explore.page.free.ask', defaultMessage: 'Задать его', description: 'Кнопка: задать предложенный ближайший готовый вопрос; коротко' },
  freeClose: { id: 'explore.page.free.close', defaultMessage: 'Закрыть', description: 'Кнопка: закрыть сообщение о свободном вопросе; коротко' },
  presetsTitle: { id: 'explore.page.presets', defaultMessage: 'Готовые вопросы', description: 'Подзаголовок над кнопками готовых вопросов на экране «ИИ-аналитика»' },
  pickTitle: { id: 'explore.page.pick.title', defaultMessage: 'Выбор контура', description: 'Заголовок карточки поиска контура для разбора' },
  pickPlaceholderSample: { id: 'explore.page.pick.placeholderSample', defaultMessage: 'Номер контура — например {no} — или его идентификатор', description: 'Подсказка в поле поиска контура; {no} — номер примера' },
  pickPlaceholder: { id: 'explore.page.pick.placeholder', defaultMessage: 'Номер контура или его идентификатор', description: 'Подсказка в поле поиска контура, когда примеров нет' },
  pickAria: { id: 'explore.page.pick.aria', defaultMessage: 'Поиск контура по номеру', description: 'aria-label поля поиска контура' },
  pickCloseCase: { id: 'explore.page.pick.closeCase', defaultMessage: 'Закрыть разбор', description: 'Кнопка рядом с поиском контура: закрыть открытый разбор контура; коротко' },
  pickHelp: {
    id: 'explore.page.pick.help',
    defaultMessage:
      'Поиск идёт по полю «номер» реестра контуров, без нечёткого совпадения: адрес разбора хранится в ссылке (<code>?case=</code>), поэтому конкретным контуром можно поделиться.',
    description: 'Подсказка под полем поиска контура; <code> — фрагмент адреса «?case=», его не переводить',
  },
  pickSamples: { id: 'explore.page.pick.samples', defaultMessage: 'Например — по порядку реестра, это не отбор «лучших»:', description: 'Подпись перед примерами номеров контуров' },
  pickChip: { id: 'explore.page.pick.chip', defaultMessage: '№ {no}', description: 'Кнопка-пример или строка результата: номер контура; {no} — номер' },
  pickNoneTitle: { id: 'explore.page.pick.noneTitle', defaultMessage: 'Контуров с таким номером нет', description: 'Заголовок пустого результата поиска контура' },
  pickNoneReason: {
    id: 'explore.page.pick.noneReason',
    defaultMessage: 'в реестре контуров текущего набора данных номер «{query}» не встречается — проверьте написание или переключите набор данных',
    description: 'Пояснение в пустом результате поиска контура (строчными); {query} — введённый номер',
  },
  pickRowMeta: { id: 'explore.page.pick.rowMeta', defaultMessage: '{farm} · {massif} · {ha} га · {crop}', description: 'Мелкая строка результата поиска контура; {farm} — хозяйство, {massif} — массив, {ha} — площадь контура в гектарах, {crop} — культура или «размещение не проставлено»' },
  pickNoPlacement: { id: 'explore.page.pick.noPlacement', defaultMessage: 'размещение не проставлено', description: 'Вместо культуры у контура, у которого размещение не проставлено; строчными' },
  notBuiltTitle: { id: 'explore.page.notBuilt.title', defaultMessage: 'Ответ не собрался', description: 'Заголовок карточки, когда ответ на готовый вопрос не получилось собрать' },
  notBuiltLoading: { id: 'explore.page.notBuilt.loading', defaultMessage: 'Считаем по срезу', description: 'Заголовок пустого состояния, пока ответ считается' },
  notBuiltMissing: { id: 'explore.page.notBuilt.missing', defaultMessage: 'Не хватает данных среза', description: 'Заголовок пустого состояния, когда часть данных среза не прочиталась' },
  notBuiltLoadingReason: { id: 'explore.page.notBuilt.loadingReason', defaultMessage: 'ответ считается из данных среза прямо в браузере — на первом открытии это занимает секунду', description: 'Пояснение, пока ответ считается (строчными)' },
  notBuiltMissingReason: { id: 'explore.page.notBuilt.missingReason', defaultMessage: 'часть данных набора не прочиталась, а печатать число, которого не считали, нельзя', description: 'Пояснение, когда часть данных не прочиталась (строчными)' },
  howTitle: { id: 'explore.page.how.title', defaultMessage: 'Как читать эти ответы', description: 'Заголовок карточки-памятки внизу экрана «ИИ-аналитика»' },
  howForm: {
    id: 'explore.page.how.form',
    defaultMessage:
      'Форма ответа фиксирована и повторяет презентацию: краткий вывод, отклонения, причины, связанные данные, рекомендуемые действия. Рецепт может не найти отклонений, но не может выкинуть блок — пустое место объясняется словами.',
    description: 'Первый пункт памятки «Как читать эти ответы»',
  },
  howClass: {
    id: 'explore.page.how.class',
    defaultMessage:
      'Каждое утверждение несёт эпистемический класс, выданный вместе с ответом: «факт среза» — число прочитано из данных как есть, «производный из среза» — посчитано по рецепту. Экран класс не переписывает и не повышает.',
    description: 'Второй пункт памятки «Как читать эти ответы»',
  },
  howCauses: {
    id: 'explore.page.how.causes',
    defaultMessage: '«Причины» собраны из дат и состояний записей — поздние отчёты, недопоставка, молчание. Это производная от данных, а не суждение о том, что произошло в поле.',
    description: 'Третий пункт памятки «Как читать эти ответы»',
  },
  howActions: {
    id: 'explore.page.how.actions',
    defaultMessage:
      '«Рекомендуемые действия» ничего не запускают: это ссылки на экраны, где решение принимает человек. Санкций, рейтингов и баллов исполнительской дисциплины прототип не считает.',
    description: 'Четвёртый пункт памятки «Как читать эти ответы»',
  },
});

/** Разрыв долей называется пунктами, а не процентами. */
function points(t: Translate, value: number): string {
  return t(m.points, { value: fmtOne(value) });
}

type Translate = ReturnType<typeof useT>;

/** Ссылка-тег ICU: `<link>текст</link>` → `<Link to=…>текст</Link>`. */
const linkTo = (to: string) => (chunks: ReactNode) => <Link to={to}>{chunks}</Link>;

/** Перечисление ссылок-фраз через разделитель: ключи расставлены, разделитель не переводится. */
function joinNodes(items: ReactNode[], separator: string): ReactNode {
  return items.map((item, i) => (
    <span key={i}>
      {i > 0 ? separator : ''}
      {item}
    </span>
  ));
}

/* ------------------------------------------------------------------ ответ */

interface Ctx {
  asOf: string;
  cycle: Cycle;
  cycleName: string;
  works: Work[];
  obligations: Obligation[];
  contourById: Map<string, ContourMeta>;
  massifById: Map<string, Massif>;
  farmById: Map<string, Farm>;
  water: WaterFile;
  quarantine: QuarantineFile;
  /** Очередь внимания: из неё берётся предмет для формы поручения. */
  attention: AttentionItem[];
  /** Перевод показанного языка: рецепты вставляют ссылки тегами ICU. */
  t: Translate;
}

interface MassifAgg {
  id: string;
  name: string;
  total: number;
  reported: number;
  verified: number;
  /** Работы закрытых окон без отчёта. */
  silent: number;
  /** Поступившие отчёты, пришедшие после закрытия окна. */
  late: number;
  areaHa: number;
  /** Доля отчитавшихся в процентах; null — делить не на что. */
  reportedShare: number | null;
}

/** Работы на полях, чьё окно уже открылось: у «впереди» нет отставания. */
function eligibleOf(ctx: Ctx): Obligation[] {
  return ctx.obligations.filter((o) => o.state !== 'upcoming');
}

function massifRows(ctx: Ctx): MassifAgg[] {
  const acc = new Map<string, MassifAgg>();
  for (const o of eligibleOf(ctx)) {
    const contour = ctx.contourById.get(o.contour_id);
    if (!contour) continue;
    let row = acc.get(contour.massif_id);
    if (!row) {
      row = {
        id: contour.massif_id,
        name: ctx.massifById.get(contour.massif_id)?.name ?? contour.massif_id,
        total: 0,
        reported: 0,
        verified: 0,
        silent: 0,
        late: 0,
        areaHa: 0,
        reportedShare: null,
      };
      acc.set(contour.massif_id, row);
    }
    row.total += 1;
    row.areaHa += o.area_ha;
    if (o.state === 'reported' || o.state === 'reported_verified') {
      row.reported += 1;
      if (o.late) row.late += 1;
    }
    if (o.state === 'reported_verified') row.verified += 1;
    if (o.state === 'silent') row.silent += 1;
  }
  const rows = [...acc.values()];
  for (const row of rows) row.reportedShare = share(row.reported, row.total);
  return rows.sort((a, b) => (a.reportedShare ?? 0) - (b.reportedShare ?? 0));
}

/**
 * Хозяйства, отсортированные по ЧИСЛУ работ на полях, а не по дисциплине.
 * Правило намеренно «размерное»: пара для сравнения подбирается по
 * сопоставимости знаменателей, а не по тому, кто хуже. Рейтинга хозяйств
 * прототип не строит — ни явно, ни через порядок в списке.
 */
function farmsByVolume(ctx: Ctx, massifId: string | null): { id: string; total: number }[] {
  const acc = new Map<string, number>();
  for (const o of ctx.obligations) {
    if (massifId) {
      const contour = ctx.contourById.get(o.contour_id);
      if (!contour || contour.massif_id !== massifId) continue;
    }
    acc.set(o.farm_id, (acc.get(o.farm_id) ?? 0) + 1);
  }
  return [...acc.entries()]
    .map(([id, total]) => ({ id, total }))
    .sort((a, b) => b.total - a.total);
}

/** Ссылка на сравнение пары хозяйств; пары нет — обычный экран сравнения. */
function comparePair(ctx: Ctx, massifId: string | null): string {
  const list = farmsByVolume(ctx, massifId);
  if (list.length < 2) return '/compare';
  return `/compare?a=${list[0]!.id}&b=${list[1]!.id}`;
}

/**
 * Сколько позиций очереди форма поручения показывает списком источников
 * (`SOURCE_LIMIT` на `/ops/assignments`). Позиция ниже этой границы заголовок
 * подставит, а привязку — нет, поэтому предметом она служить не может.
 */
const ATTENTION_SOURCE_LIMIT = 10;

/**
 * Действие «создать поручение» с предметом. Форма подставляет заголовок,
 * описание и привязку по позиции очереди внимания (`?from=<id>`) — и только
 * по той, которую она сама показывает источником. Когда позиции с этим
 * предметом в срезе нет, ссылка идёт без `from`, а предмет называется
 * словами в подсказке: пустое последнее звено разбора хуже честной оговорки.
 */
function assignAction(
  ctx: Ctx,
  spec: {
    label: string;
    /** Предмет словами — он же уходит в оговорку, когда позиции нет. */
    subject: string;
    /**
     * Правила поиска позиции по убыванию точности: сначала точное совпадение
     * предмета, затем — позиция того же рода. Пустой список значит, что позиции
     * такого рода в очереди не бывает вовсе.
     */
    match?: ((item: AttentionItem) => boolean)[];
  },
): AnswerAction {
  const queue = [...ctx.attention]
    .sort((a, b) => a.rank - b.rank)
    .slice(0, ATTENTION_SOURCE_LIMIT);

  let source: AttentionItem | null = null;
  for (const rule of spec.match ?? []) {
    source = queue.find(rule) ?? null;
    if (source) break;
  }

  return {
    label: spec.label,
    to: source ? `/ops/assignments?from=${source.id}` : '/ops/assignments',
    hint: source
      ? ctx.t(m.assignHintSource, { title: source.title })
      : ctx.t(m.assignHintNone, { subject: spec.subject }),
    icon: ClipboardList,
  };
}

/* --------------------------------------------------------------- рецепты */

function recipeMassifLag(ctx: Ctx): Answer {
  const { t } = ctx;
  const rows = massifRows(ctx);
  const eligible = eligibleOf(ctx);
  const reported = eligible.filter(
    (o) => o.state === 'reported' || o.state === 'reported_verified',
  ).length;
  const late = eligible.filter(
    (o) => o.late && (o.state === 'reported' || o.state === 'reported_verified'),
  ).length;
  const silent = eligible.filter((o) => o.state === 'silent').length;
  const silentFarms = new Set(
    eligible.filter((o) => o.state === 'silent').map((o) => o.farm_id),
  ).size;
  const upcoming = ctx.obligations.length - eligible.length;
  const worst = rows.slice(0, 3);
  const best = rows[rows.length - 1];
  const gap =
    best && worst[0] && best.reportedShare !== null && worst[0].reportedShare !== null
      ? best.reportedShare - worst[0].reportedShare
      : null;
  const lagId = worst[0]?.id ?? null;
  const derivedLabel = `${DERIVED} ${dateShort(ctx.asOf)}`;

  const actions: AnswerAction[] = [
    assignAction(ctx, {
      label: worst[0] ? t(m.lagAssign, { name: worst[0].name }) : t(m.lagAssignAny),
      subject: worst[0]
        ? t(m.lagSubject, { name: worst[0].name, cycle: ctx.cycleName })
        : t(m.subjectCycle, { cycle: ctx.cycleName }),
    }),
    {
      label: t(m.openCycle, { cycle: ctx.cycleName }),
      to: `/cycles/${ctx.cycle.id}`,
      hint: t(m.lagOpenHint),
      icon: ListTree,
    },
    {
      label: t(m.compareTwo),
      to: comparePair(ctx, lagId),
      hint: t(m.compareHint),
      icon: GitCompare,
    },
  ];

  return {
    title: t(m.lagTitle),
    summary: {
      evidence: 'derived',
      evidenceLabel: derivedLabel,
      text: (
        <>
          {t(m.lagSummaryOverall, {
            share: pct(share(reported, eligible.length), 1),
            cycle: ctx.cycleName,
            reported: num(reported),
            total: eligible.length,
          })}{' '}
          {worst[0]
            ? t(m.lagSummaryWorst, {
              link: linkTo(`/massifs/${worst[0].id}`),
              name: worst[0].name,
              share: pct(worst[0].reportedShare, 1),
              total: worst[0].total,
              hasGap: gap !== null ? 'yes' : 'no',
              gap: gap !== null ? points(t, gap) : '',
            })
            : null}
        </>
      ),
    },
    deviations: [
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text:
          worst.length === 0 ? (
            <>{t(m.lagDevNoRows)}</>
          ) : (
            <>
              {t(m.lagDevWorst, {
                list: () =>
                  joinNodes(
                    worst.map((row) =>
                      t(m.lagDevWorstItem, {
                        link: linkTo(`/massifs/${row.id}`),
                        name: row.name,
                        share: pct(row.reportedShare, 1),
                        total: row.total,
                        ha: ha(row.areaHa),
                      }),
                    ),
                    ', ',
                  ),
              })}{' '}
              {best && gap !== null
                ? t(m.lagDevBest, {
                  link: linkTo(`/massifs/${best.id}`),
                  name: best.name,
                  share: pct(best.reportedShare, 1),
                  total: best.total,
                })
                : null}
            </>
          ),
      },
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: (
          <>
            {t(m.lagDevRest, {
              rest: countOf(eligible.length - reported, WORDS.obligation),
              silent: num(silent),
              farms: silentFarms,
            })}
          </>
        ),
      },
    ],
    causes: [
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: <>{t(m.lagCauseLate, { late: num(late), reported: num(reported), share: pct(share(late, reported), 1) })}</>,
      },
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: <>{t(m.lagCauseSilent, { silent: countOf(silent, WORDS.obligation) })}</>,
      },
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: <>{t(m.lagCauseDenominator, { upcoming: countOf(upcoming, WORDS.obligation) })}</>,
      },
    ],
    modules: ['cycles', 'farms', 'verify', 'assignments'],
    sources: [FILES.obligations(ctx.cycle.id), FILES.contours, FILES.massifs],
    method: t(m.lagMethod),
    actions,
  };
}

function recipeWaterGap(ctx: Ctx): Answer {
  const { t } = ctx;
  const rows = new Map<
    string,
    { id: string; name: string; requested: number; delivered: number; n: number }
  >();
  for (const r of ctx.water.rows) {
    let row = rows.get(r.massif_id);
    if (!row) {
      row = {
        id: r.massif_id,
        name: ctx.massifById.get(r.massif_id)?.name ?? r.massif_id,
        requested: 0,
        delivered: 0,
        n: 0,
      };
      rows.set(r.massif_id, row);
    }
    row.requested += r.requested_m3;
    row.delivered += r.delivered_m3;
    row.n += 1;
  }
  const list = [...rows.values()]
    .map((row) => ({ ...row, coverage: share(row.delivered, row.requested) }))
    .sort((a, b) => (a.coverage ?? 0) - (b.coverage ?? 0));
  const worst = list.slice(0, 3);
  const pending = ctx.water.rows.filter((r) => r.status === 'pending').length;
  const partial = ctx.water.rows.filter((r) => r.status === 'partial').length;
  const gapM3 = ctx.water.summary.requested_m3 - ctx.water.summary.delivered_m3;
  const derivedLabel = `${DERIVED} ${dateShort(ctx.asOf)}`;

  const irrigation = ctx.works
    .filter((w) => w.kind === 'irrigation')
    .sort((a, b) => b.totals.obligations - a.totals.obligations);
  const irrigationOpen = irrigation.find((w) => w.status !== 'done') ?? irrigation[0] ?? null;

  const actions: AnswerAction[] = [
    irrigationOpen
      ? {
          label: t(m.waterOpenWork, { name: irrigationOpen.name }),
          to: `/cycles/${ctx.cycle.id}/works/${irrigationOpen.id}`,
          hint: t(m.waterOpenWorkHint),
          icon: Droplets,
        }
      : {
          label: t(m.openCycle, { cycle: ctx.cycleName }),
          to: `/cycles/${ctx.cycle.id}`,
          hint: t(m.waterOpenCycleHint),
          icon: ListTree,
        },
    {
      label: worst[0] ? t(m.waterOpenMassif, { name: worst[0].name }) : t(m.waterOpenMassifAny),
      to: worst[0] ? `/massifs/${worst[0].id}` : '/',
      hint: t(m.waterOpenMassifHint),
      icon: Layers,
    },
    assignAction(ctx, {
      label: t(m.waterAssign),
      subject: worst[0]
        ? t(m.waterSubjectMassif, { name: worst[0].name, cycle: ctx.cycleName })
        : t(m.waterSubjectCycle, { cycle: ctx.cycleName }),
    }),
  ];

  return {
    title: t(m.waterTitle),
    summary: {
      evidence: 'fact',
      evidenceLabel: `${FACT} ${dateShort(ctx.asOf)}`,
      text: (
        <>
          {t(m.waterSummary, {
            cycle: ctx.cycleName,
            requested: m3(ctx.water.summary.requested_m3),
            delivered: m3(ctx.water.summary.delivered_m3),
            coverage: pct(ctx.water.summary.coverage_pct, 1),
            requests: ctx.water.summary.requests,
            gap: m3(gapM3),
          })}
        </>
      ),
    },
    deviations:
      list.length === 0
        ? []
        : [
            {
              evidence: 'derived',
              evidenceLabel: derivedLabel,
              text: (
                <>
                  {t(m.waterDevWorst, {
                    list: () =>
                      joinNodes(
                        worst.map((row) =>
                          t(m.waterDevWorstItem, {
                            link: linkTo(`/massifs/${row.id}`),
                            name: row.name,
                            coverage: pct(row.coverage, 1),
                            delivered: m3(row.delivered),
                            requested: m3(row.requested),
                            n: row.n,
                          }),
                        ),
                        ', ',
                      ),
                  })}
                </>
              ),
            },
            {
              evidence: 'derived',
              evidenceLabel: derivedLabel,
              text: (
                <>
                  {t(m.waterDevStatus, { total: ctx.water.rows.length, partial: num(partial), pending: num(pending) })}
                </>
              ),
            },
          ],
    deviationsEmpty: t(m.waterDevEmpty),
    causes: [
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: (
          <>
            {t(m.waterCauseUneven, {
              gap: points(t, Math.abs((list[list.length - 1]?.coverage ?? 0) - (list[0]?.coverage ?? 0))),
            })}
          </>
        ),
      },
      {
        evidence: 'demo',
        evidenceLabel: t(m.noteAssumption),
        text: <>{t(m.waterCauseAssumption)}</>,
      },
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: <>{t(m.waterCauseNoData)}</>,
      },
    ],
    modules: ['water', 'cycles', 'farms', 'assignments'],
    sources: [FILES.water(ctx.cycle.id), FILES.massifs],
    method: t(m.waterMethod),
    actions,
  };
}

function recipeWindows(ctx: Ctx): Answer {
  const { t } = ctx;
  const horizon = ctx.works.filter((w) => w.status !== 'done');
  const closing = horizon
    .filter((w) => {
      const left = daysBetween(ctx.asOf, w.window.to);
      return left >= 0 && left <= WEEK_DAYS;
    })
    .sort((a, b) => a.window.to.localeCompare(b.window.to));
  const opening = horizon
    .filter((w) => {
      const till = daysBetween(ctx.asOf, w.window.from);
      return till > 0 && till <= WEEK_DAYS;
    })
    .sort((a, b) => a.window.from.localeCompare(b.window.from));
  const later = horizon.filter((w) => !closing.includes(w) && !opening.includes(w));
  const silent = ctx.obligations.filter((o) => o.state === 'silent').length;
  const first = closing[0] ?? opening[0] ?? later[0] ?? null;
  const derivedLabel = `${DERIVED} ${dateShort(ctx.asOf)}`;

  const deviations: Claim[] = [];
  if (closing.length > 0) {
    deviations.push({
      evidence: 'derived',
      evidenceLabel: derivedLabel,
      text: (
        <>
          {t(m.winClosing, {
            list: () =>
              joinNodes(
                closing.slice(0, LIST_LIMIT).map((w) =>
                  t(m.winClosingItem, {
                    link: linkTo(`/cycles/${ctx.cycle.id}/works/${w.id}`),
                    name: w.name,
                    date: dateShort(w.window.to),
                    days: Math.max(0, daysBetween(ctx.asOf, w.window.to)),
                    reported: num(w.totals.reported),
                    total: w.totals.obligations,
                    share: pctAxis(share(w.totals.reported, w.totals.obligations)),
                    ha: ha(w.totals.area_ha),
                  }),
                ),
                '; ',
              ),
            hasMore: closing.length > LIST_LIMIT ? 'yes' : 'no',
            more: num(closing.length - LIST_LIMIT),
          })}
        </>
      ),
    });
  }
  if (opening.length > 0) {
    deviations.push({
      evidence: 'derived',
      evidenceLabel: derivedLabel,
      text: (
        <>
          {t(m.winOpening, {
            list: () =>
              joinNodes(
                opening.slice(0, LIST_LIMIT).map((w) =>
                  t(m.winOpeningItem, {
                    link: linkTo(`/cycles/${ctx.cycle.id}/works/${w.id}`),
                    name: w.name,
                    date: dateShort(w.window.from),
                    works: countOf(w.totals.obligations, WORDS.obligation),
                    ha: ha(w.totals.area_ha),
                  }),
                ),
                ', ',
              ),
            link: linkTo('/ops/campaigns'),
          })}
        </>
      ),
    });
  }

  return {
    title: t(m.winTitle),
    summary: {
      evidence: 'derived',
      evidenceLabel: derivedLabel,
      text:
        closing.length === 0 && opening.length === 0 ? (
          <>{t(m.winSummaryNone, { date: dateShort(ctx.asOf), cycle: ctx.cycleName, count: num(horizon.length) })}</>
        ) : (
          <>
            {t(m.winSummary, {
              date: dateShort(ctx.asOf),
              cycle: ctx.cycleName,
              closing: countOf(closing.length, WORDS.work),
              opening: countOf(opening.length, WORDS.work),
              total: num(horizon.length),
            })}
          </>
        ),
    },
    deviations,
    deviationsEmpty: t(m.winDevEmpty, { date: dateShort(ctx.asOf) }),
    causes: [
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: <>{t(m.winCauseNotLag)}</>,
      },
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: <>{t(m.winCausePast, { silent: countOf(silent, WORDS.obligation) })}</>,
      },
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: <>{t(m.winCauseHorizon, { date: dateLong(ctx.asOf) })}</>,
      },
    ],
    modules: ['cycles', 'assignments', 'verify'],
    sources: [FILES.works(ctx.cycle.id), FILES.meta],
    method: t(m.winMethod),
    actions: [
      first
        ? {
            label: t(m.winOpenWork, { name: first.name }),
            to: `/cycles/${ctx.cycle.id}/works/${first.id}`,
            hint: t(m.winOpenWorkHint),
            icon: Timer,
          }
        : {
            label: t(m.openCycle, { cycle: ctx.cycleName }),
            to: `/cycles/${ctx.cycle.id}`,
            hint: t(m.winOpenCycleHint),
            icon: ListTree,
          },
      {
        label: t(m.winCampaign),
        to: '/ops/campaigns',
        hint: t(m.winCampaignHint),
        icon: MapPinned,
      },
      assignAction(ctx, {
        label: t(m.winAssign),
        subject: first
          ? t(m.winSubjectWork, { name: first.name, date: dateShort(first.window.to) })
          : t(m.subjectCycle, { cycle: ctx.cycleName }),
        match: first
          ? [
              (item) => item.kind === 'window_closing' && item.ref.work_id === first.id,
              (item) => item.kind === 'window_closing',
            ]
          : undefined,
      }),
    ],
  };
}

function recipeQuarantine(ctx: Ctx): Answer {
  const { t } = ctx;
  // Считаем покрытие по контурам ЦИКЛА: журнал может держать и чужие записи,
  // а знаменатель ответа обязан совпадать с тем, о чём спросили.
  const inspectedIds = new Set(
    ctx.quarantine.inspections
      .filter((i) => ctx.contourById.get(i.contour_id)?.crop_2026 === ctx.cycle.crop)
      .map((i) => i.contour_id),
  );
  const findings = ctx.quarantine.inspections.filter((i) => i.result === 'finding');
  const byKind = new Map<string, number>();
  const byName = new Map<string, number>();
  for (const f of findings) {
    if (!f.finding) continue;
    byKind.set(f.finding.kind, (byKind.get(f.finding.kind) ?? 0) + 1);
    byName.set(f.finding.name, (byName.get(f.finding.name) ?? 0) + 1);
  }
  const topNames = [...byName.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);
  const kinds = [...byKind.entries()].sort((a, b) => b[1] - a[1]);
  const cycleContours = ctx.cycle.contours;
  const uncovered = Math.max(0, cycleContours - inspectedIds.size);
  const watch = ctx.quarantine.statuses
    .filter((s) => s.status !== 'green')
    .sort((a, b) => a.since.localeCompare(b.since));
  const freshest = watch.length > 0 ? watch[watch.length - 1] : null;
  const derivedLabel = `${DERIVED} ${dateShort(ctx.asOf)}`;
  const summary = ctx.quarantine.summary;

  return {
    title: t(m.quTitle),
    summary: {
      evidence: 'fact',
      evidenceLabel: `${FACT} ${dateShort(ctx.asOf)}`,
      text: (
        <>
          {summary.inspections === null
            ? t(m.quSummaryNoInspections, {
              green: num(summary.contours_green),
              watch: num(summary.contours_watch),
              note: ctx.quarantine.inspections_note ?? t(m.quNoJournal),
            })
            : t(m.quSummaryInspections, {
              green: num(summary.contours_green),
              watch: num(summary.contours_watch),
              inspections: summary.inspections,
              coverage: pct(summary.coverage_pct, 1),
            })}{' '}
          {findings.length > 0
            ? t(m.quSummaryFindings, {
              count: findings.length,
              names: topNames.map(([name, n]) => `${name} (${num(n)})`).join(', '),
            })
            : t(m.quSummaryNoFindings)}
        </>
      ),
    },
    deviations: [
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: (
          <>
            {t(m.quDevCoverage, {
              inspected: inspectedIds.size,
              total: cycleContours,
              uncovered: num(uncovered),
            })}
          </>
        ),
      },
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: (
          <>
            {t(m.quDevWatch, {
              watch: countOf(watch.length, WORDS.contour),
              hasFreshest: freshest ? 'yes' : 'no',
              date: freshest ? dateShort(freshest.since) : '',
              link: linkTo(freshest ? `/contours/${freshest.contour_id}` : '/contours'),
              hasCause: freshest?.cause ? 'yes' : 'no',
              cause: freshest?.cause ?? '',
            })}
          </>
        ),
      },
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text:
          kinds.length > 0 ? (
            <>{t(m.quDevKinds, { kinds: kinds.map(([kind, n]) => `${kind} — ${num(n)}`).join(', ') })}</>
          ) : (
            <>{t(m.quDevKindsNone)}</>
          ),
      },
    ],
    causes: [
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: <>{t(m.quCauseCoverage, { coverage: pct(summary.coverage_pct, 1) })}</>,
      },
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: <>{t(m.quCauseWatch)}</>,
      },
    ],
    modules: ['quarantine', 'contours', 'map', 'assignments'],
    sources: [FILES.quarantine, FILES.cycles],
    method: t(m.quMethod),
    actions: [
      freshest
        ? {
            label: t(m.quActionCase),
            to: `/explore?preset=case&case=${freshest.contour_id}`,
            hint: t(m.quActionCaseHint),
            icon: FileSearch,
          }
        : {
            label: t(m.quActionRegistry),
            to: '/contours',
            hint: t(m.quActionRegistryHint),
            icon: Layers,
          },
      {
        label: t(m.quActionMap),
        to: '/map',
        hint: t(m.quActionMapHint),
        icon: MapPinned,
      },
      assignAction(ctx, {
        label: t(m.quActionAssign),
        subject: freshest
          ? t(m.quSubjectContour, { no: ctx.contourById.get(freshest.contour_id)?.no ?? freshest.contour_id })
          : t(m.quSubjectAny),
        // Точное совпадение по контуру свежего очага — если он в очереди;
        // иначе любая позиция наблюдения: предмет остаётся карантинным, и
        // подставленная привязка ведёт на реальный контур под наблюдением.
        match: freshest
          ? [
              (item) =>
                item.kind === 'quarantine_watch' && item.ref.contour_id === freshest.contour_id,
              (item) => item.kind === 'quarantine_watch',
            ]
          : [(item) => item.kind === 'quarantine_watch'],
      }),
    ],
  };
}

function recipeCompare(ctx: Ctx): Answer {
  const { t } = ctx;
  const rows = massifRows(ctx).filter((r) => r.total >= COMPARE_FLOOR);
  const low = rows[0] ?? null;
  const high = rows[rows.length - 1] ?? null;
  const derivedLabel = `${DERIVED} ${dateShort(ctx.asOf)}`;

  if (!low || !high || low.id === high.id) {
    return {
      title: t(m.cmpTitleNone),
      summary: {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: <>{t(m.cmpSummaryNone, { count: COMPARE_FLOOR })}</>,
      },
      deviations: [],
      deviationsEmpty: t(m.cmpDevEmpty),
      causes: [
        {
          evidence: 'derived',
          evidenceLabel: derivedLabel,
          text: <>{t(m.cmpCauseFloor, { floor: countOf(COMPARE_FLOOR, WORDS.obligation) })}</>,
        },
      ],
      modules: ['cycles', 'farms'],
      sources: [FILES.obligations(ctx.cycle.id), FILES.contours, FILES.massifs],
      method: t(m.cmpMethodNone),
      actions: [
        {
          label: t(m.compareTwo),
          to: '/compare',
          hint: t(m.cmpActionPickHint),
          icon: GitCompare,
        },
      ],
    };
  }

  const gap = (high.reportedShare ?? 0) - (low.reportedShare ?? 0);
  const ratio =
    low.total > 0 && high.total > 0
      ? Math.max(low.total, high.total) / Math.min(low.total, high.total)
      : 1;

  return {
    title: t(m.cmpTitle, { low: low.name, high: high.name }),
    summary: {
      evidence: 'derived',
      evidenceLabel: derivedLabel,
      text: (
        <>
          {t(m.cmpSummary, {
            date: dateLong(ctx.asOf),
            cycle: ctx.cycleName,
            lowLink: linkTo(`/massifs/${low.id}`),
            low: low.name,
            lowShare: pct(low.reportedShare, 1),
            lowTotal: low.total,
            highLink: linkTo(`/massifs/${high.id}`),
            high: high.name,
            highShare: pct(high.reportedShare, 1),
            highTotal: high.total,
            gap: points(t, gap),
          })}
        </>
      ),
    },
    deviations: [
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: (
          <>
            {t(m.cmpDevLow, {
              link: linkTo(`/massifs/${low.id}`),
              name: low.name,
              share: pct(low.reportedShare, 1),
              total: low.total,
              ha: ha(low.areaHa),
              silent: num(low.silent),
              late: num(low.late),
            })}
          </>
        ),
      },
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: (
          <>
            {t(m.cmpDevHigh, {
              link: linkTo(`/massifs/${high.id}`),
              name: high.name,
              share: pct(high.reportedShare, 1),
              total: high.total,
              ha: ha(high.areaHa),
              silent: num(high.silent),
              late: num(high.late),
            })}
          </>
        ),
      },
    ],
    causes: [
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: <>{t(m.cmpCauseRatio, { ratio: fmtOne(ratio) })}</>,
      },
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: (
          <>
            {t(m.cmpCauseSplit, {
              lowSilent: num(low.silent),
              highSilent: num(high.silent),
              lowLate: num(low.late),
              highLate: num(high.late),
            })}
          </>
        ),
      },
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: <>{t(m.cmpCauseNoScore)}</>,
      },
    ],
    modules: ['cycles', 'farms', 'verify'],
    sources: [FILES.obligations(ctx.cycle.id), FILES.contours, FILES.massifs],
    method: t(m.cmpMethod, { floor: num(COMPARE_FLOOR) }),
    actions: [
      {
        label: t(m.waterOpenMassif, { name: low.name }),
        to: `/massifs/${low.id}`,
        hint: t(m.cmpOpenLowHint),
        icon: Layers,
      },
      {
        label: t(m.waterOpenMassif, { name: high.name }),
        to: `/massifs/${high.id}`,
        hint: t(m.cmpOpenHighHint),
        icon: Layers,
      },
      {
        label: t(m.cmpSideBySide),
        to: comparePair(ctx, low.id),
        hint: t(m.compareHint),
        icon: GitCompare,
      },
    ],
  };
}

function recipeSilentFarms(ctx: Ctx): Answer {
  const { t } = ctx;
  const silent = ctx.obligations.filter((o) => o.state === 'silent');
  const acc = new Map<string, { id: string; count: number; areaHa: number; lastClose: string }>();
  const workById = new Map(ctx.works.map((w) => [w.id, w]));
  for (const o of silent) {
    let row = acc.get(o.farm_id);
    if (!row) {
      row = { id: o.farm_id, count: 0, areaHa: 0, lastClose: '' };
      acc.set(o.farm_id, row);
    }
    row.count += 1;
    row.areaHa += o.area_ha;
    const close = workById.get(o.work_id)?.window.to ?? '';
    if (close > row.lastClose) row.lastClose = close;
  }
  const rows = [...acc.values()].sort((a, b) => b.count - a.count);
  const top = rows.slice(0, LIST_LIMIT);
  const cycleFarms = new Set(ctx.obligations.map((o) => o.farm_id)).size;
  const late = ctx.obligations.filter(
    (o) => o.late && (o.state === 'reported' || o.state === 'reported_verified'),
  ).length;
  const oldest = rows
    .map((r) => r.lastClose)
    .filter(Boolean)
    .sort()[0];
  const derivedLabel = `${DERIVED} ${dateShort(ctx.asOf)}`;

  return {
    title: t(m.siTitle),
    summary: {
      evidence: 'derived',
      evidenceLabel: derivedLabel,
      text:
        silent.length === 0 ? (
          <>{t(m.siSummaryNone, { cycle: ctx.cycleName })}</>
        ) : (
          <>
            {t(m.siSummary, {
              cycle: ctx.cycleName,
              silent: countOf(silent.length, WORDS.obligation),
              rows: rows.length,
              total: cycleFarms,
            })}
          </>
        ),
    },
    deviations:
      rows.length === 0
        ? []
        : [
            {
              evidence: 'derived',
              evidenceLabel: derivedLabel,
              text: (
                <>
                  {t(m.siDevTop, {
                    list: () =>
                      joinNodes(
                        top.map((row) =>
                          t(m.siDevTopItem, {
                            link: linkTo(`/farms/${row.id}`),
                            name: ctx.farmById.get(row.id)?.name ?? row.id,
                            works: countOf(row.count, WORDS.obligation),
                            ha: ha(row.areaHa),
                          }),
                        ),
                        ', ',
                      ),
                    hasMore: rows.length > LIST_LIMIT ? 'yes' : 'no',
                    total: num(rows.length),
                  })}
                </>
              ),
            },
            {
              evidence: 'derived',
              evidenceLabel: derivedLabel,
              text: (
                <>
                  {t(m.siDevOldest, {
                    date: oldest ? dateShort(oldest) : '—',
                    count: num(Math.max(0, cycleFarms - rows.length)),
                  })}
                </>
              ),
            },
          ],
    deviationsEmpty: t(m.siDevEmpty),
    causes: [
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: <>{t(m.siCauseWindow)}</>,
      },
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: <>{t(m.siCauseLate, { late })}</>,
      },
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: <>{t(m.siCauseNotDone)}</>,
      },
    ],
    modules: ['farms', 'cycles', 'assignments', 'verify'],
    sources: [FILES.obligations(ctx.cycle.id), FILES.farms],
    method: t(m.siMethod),
    actions: [
      {
        label: t(m.siActionEntry),
        to: '/ops/entry',
        hint: t(m.siActionEntryHint),
        icon: NotebookPen,
      },
      assignAction(ctx, {
        label: t(m.siActionAssign),
        subject: top[0]
          ? t(m.siSubjectFarm, { name: ctx.farmById.get(top[0].id)?.name ?? top[0].id, cycle: ctx.cycleName })
          : t(m.subjectCycle, { cycle: ctx.cycleName }),
        match: top[0]
          ? [
              (item) => item.kind === 'silent_farm' && item.ref.farm_id === top[0]!.id,
              (item) => item.kind === 'silent_farm',
            ]
          : [(item) => item.kind === 'silent_farm'],
      }),
      {
        label: t(m.compareTwo),
        to: comparePair(ctx, null),
        hint: t(m.compareHint),
        icon: GitCompare,
      },
    ],
  };
}

/* --------------------------------------------------------------- пресеты */

type PresetId =
  | 'massif-lag'
  | 'water-gap'
  | 'windows'
  | 'quarantine'
  | 'compare'
  | 'silent'
  | 'case';

const PRESET_IDS: PresetId[] = [
  'massif-lag',
  'water-gap',
  'windows',
  'quarantine',
  'compare',
  'silent',
  'case',
];

function isPresetId(value: string | null): value is PresetId {
  return value !== null && (PRESET_IDS as string[]).includes(value);
}

interface Preset {
  id: PresetId;
  icon: LucideIcon;
  question: string;
  why: string;
  keywords: string[];
  /** Пресет «Разбор контура» не считает ответ — он открывает выбор номера. */
  run: ((ctx: Ctx) => Answer) | null;
}

function buildPresets(ctx: Ctx | null, t: Translate): Preset[] {
  const compareNames = (() => {
    if (!ctx) return null;
    const rows = massifRows(ctx).filter((r) => r.total >= COMPARE_FLOOR);
    if (rows.length < 2) return null;
    return [rows[0]!.name, rows[rows.length - 1]!.name] as const;
  })();

  return [
    {
      id: 'massif-lag',
      icon: TrendingDown,
      question: t(m.presetLagQ),
      why: t(m.presetLagWhy),
      // i18n-ignore: корни слов для сопоставления с набранным вопросом, не текст интерфейса
      keywords: ['массив', 'отстаю', 'дисциплин', 'отчёт', 'отчет', 'хуже'],
      run: recipeMassifLag,
    },
    {
      id: 'water-gap',
      icon: Droplets,
      question: t(m.presetWaterQ),
      why: t(m.presetWaterWhy),
      // i18n-ignore: корни слов для сопоставления с набранным вопросом, не текст интерфейса
      keywords: ['вод', 'полив', 'подач', 'м3', 'куб'],
      run: recipeWaterGap,
    },
    {
      id: 'windows',
      icon: Timer,
      question: t(m.presetWindowsQ),
      why: t(m.presetWindowsWhy),
      // i18n-ignore: корни слов для сопоставления с набранным вопросом, не текст интерфейса
      keywords: ['окн', 'недел', 'срок', 'закрыва', 'дедлайн'],
      run: recipeWindows,
    },
    {
      id: 'quarantine',
      icon: Bug,
      question: t(m.presetQuarantineQ),
      why: t(m.presetQuarantineWhy),
      // i18n-ignore: корни слов для сопоставления с набранным вопросом, не текст интерфейса
      keywords: ['карантин', 'вредител', 'болезн', 'сорняк', 'обследован'],
      run: recipeQuarantine,
    },
    {
      id: 'compare',
      icon: Scale,
      question: compareNames
        ? t(m.presetCompareQ, { a: compareNames[0], b: compareNames[1] })
        : t(m.presetCompareQAny),
      why: t(m.presetCompareWhy),
      // i18n-ignore: корни слов для сопоставления с набранным вопросом, не текст интерфейса
      keywords: ['сравн', 'против', 'разниц', 'кто лучше'],
      run: recipeCompare,
    },
    {
      id: 'silent',
      icon: MessageSquareOff,
      question: t(m.presetSilentQ),
      why: t(m.presetSilentWhy),
      // i18n-ignore: корни слов для сопоставления с набранным вопросом, не текст интерфейса
      keywords: ['молч', 'хозяйств', 'без отчёта', 'без отчета', 'фермер'],
      run: recipeSilentFarms,
    },
    {
      id: 'case',
      icon: FileSearch,
      question: t(m.presetCaseQ),
      why: t(m.presetCaseWhy),
      // i18n-ignore: корни слов для сопоставления с набранным вопросом, не текст интерфейса
      keywords: ['контур', 'разбор', 'номер', 'участок', 'кейс'],
      run: null,
    },
  ];
}

/** Ближайший пресет по совпадению слов. Это подсказка, а не разбор вопроса. */
function closestPreset(text: string, presets: Preset[]): Preset {
  const q = text.trim().toLowerCase();
  let best = presets[0]!;
  let bestScore = 0;
  for (const preset of presets) {
    const score = preset.keywords.reduce((sum, k) => (q.includes(k) ? sum + 1 : sum), 0);
    if (score > bestScore) {
      best = preset;
      bestScore = score;
    }
  }
  return best;
}

/* ------------------------------------------------------------------ экран */

export function Analytics() {
  const t = useT();
  const meta = useTrackData<Meta>(FILES.meta);
  const cycles = useTrackData<Cycle[]>(FILES.cycles);
  const crops = useTrackData<Crop[]>(FILES.crops);
  const massifs = useTrackData<Massif[]>(FILES.massifs);
  const farms = useTrackData<Farm[]>(FILES.farms);
  const contours = useTrackData<ContourMeta[]>(FILES.contours);
  const quarantine = useTrackData<QuarantineFile>(FILES.quarantine);
  const attention = useTrackData<AttentionItem[]>(FILES.attention);

  const asOf = meta.data?.as_of ?? AS_OF_FALLBACK;
  const district = srv(meta.data?.district) ?? t(m.districtName);
  const cycle = (cycles.data ?? []).find((c) => c.status === 'active') ?? null;
  const cycleId = cycle?.id ?? null;

  const works = useTrackData<Work[]>(cycleId ? FILES.works(cycleId) : null);
  const obligations = useTrackData<Obligation[]>(cycleId ? FILES.obligations(cycleId) : null);
  const water = useTrackData<WaterFile>(cycleId ? FILES.water(cycleId) : null);

  /*
   * Вопрос и кейс живут в адресе, а не в стейте: ссылкой на конкретный ответ
   * можно поделиться, и она переживает перезагрузку. Свободный ввод в адрес не
   * попадает — его прототип всё равно не разбирает.
   */
  const [params, setParams] = useSearchParams();
  const caseId = params.get('case');
  const rawPreset = params.get('preset');
  const asked: PresetId | null = caseId ? 'case' : isPresetId(rawPreset) ? rawPreset : null;

  const [draft, setDraft] = useState('');
  const [freeText, setFreeText] = useState<string | null>(null);
  const [pick, setPick] = useState('');

  usePageTrail(
    caseId
      ? [
          { level: t(m.trailDistrict), name: district, to: '/' },
          { level: t(m.trailSection), name: t(m.trailSectionName), to: '/explore' },
          { level: t(m.trailContour), name: t(m.trailCase) },
        ]
      : [
          { level: t(m.trailDistrict), name: district, to: '/' },
          { level: t(m.trailSection), name: t(m.trailSectionName) },
        ],
  );

  const ctx: Ctx | null = useMemo(() => {
    if (
      !cycle ||
      !works.data ||
      !obligations.data ||
      !contours.data ||
      !massifs.data ||
      !farms.data ||
      !water.data ||
      !quarantine.data
    ) {
      return null;
    }
    const contourById = new Map<string, ContourMeta>();
    for (const c of contours.data) contourById.set(c.id, c);
    const massifById = new Map<string, Massif>();
    for (const massif of massifs.data) massifById.set(massif.id, massif);
    const farmById = new Map<string, Farm>();
    for (const f of farms.data) farmById.set(f.id, f);
    return {
      asOf,
      cycle,
      cycleName: cycleTitle(cycle, crops.data),
      works: works.data,
      obligations: obligations.data,
      contourById,
      massifById,
      farmById,
      water: water.data,
      quarantine: quarantine.data,
      // Очередь внимания ответ не блокирует: без неё действия идут без
      // `?from=` и называют предмет словами.
      attention: attention.data ?? [],
      t,
    };
  }, [
    asOf,
    cycle,
    crops.data,
    works.data,
    obligations.data,
    contours.data,
    massifs.data,
    farms.data,
    water.data,
    quarantine.data,
    attention.data,
    t,
  ]);

  const presets = useMemo(() => buildPresets(ctx, t), [ctx, t]);

  const answer = useMemo(() => {
    if (!ctx || !asked) return null;
    const preset = presets.find((p) => p.id === asked);
    return preset?.run ? preset.run(ctx) : null;
  }, [ctx, asked, presets]);

  /**
   * Примеры для быстрого старта разбора: два контура, у которых заполнены ВСЕ
   * сезоны реестра (у них история культур не пустая), и два контура культуры
   * активного цикла (у них есть идущие работы). Внутри каждой группы берётся
   * порядок файла — это не отбор «лучших»: рейтингов прототип не строит.
   */
  const samples = useMemo(() => {
    const list = contours.data;
    if (!list || list.length === 0) return [];
    const keys = seasonsOf(list[0]!).map((s) => s.key);
    const filled = list.filter((c) => keys.every((k) => cropAt(c, k) !== null));
    const active = cycle ? list.filter((c) => c.crop_2026 === cycle.crop) : [];
    const out: ContourMeta[] = [];
    for (const c of [...filled.slice(0, 2), ...active.slice(0, 2)]) {
      if (!out.some((x) => x.id === c.id)) out.push(c);
    }
    return out.slice(0, 4);
  }, [contours.data, cycle]);

  /** Поиск контура по номеру или идентификатору. Порядок — по номеру. */
  const found = useMemo(() => {
    const q = pick.trim().toLowerCase();
    if (q.length === 0 || !contours.data) return [];
    const hits: ContourMeta[] = [];
    for (const c of contours.data) {
      if (c.id.toLowerCase() === q || c.no.toLowerCase().includes(q)) {
        hits.push(c);
        if (hits.length >= PICK_LIMIT * 4) break;
      }
    }
    return hits
      .sort((a, b) => a.no.localeCompare(b.no, runtimeLocale(), { numeric: true }))
      .slice(0, PICK_LIMIT);
  }, [pick, contours.data]);

  const loading =
    meta.state === 'loading' ||
    cycles.state === 'loading' ||
    works.state === 'loading' ||
    obligations.state === 'loading' ||
    contours.state === 'loading' ||
    water.state === 'loading' ||
    quarantine.state === 'loading';

  const askPreset = (id: PresetId): void => {
    setFreeText(null);
    if (id === 'case' && caseId) {
      setParams({ preset: 'case', case: caseId });
      return;
    }
    setParams({ preset: id });
  };

  const openCase = (contourId: string): void => {
    setFreeText(null);
    setParams({ preset: 'case', case: contourId });
  };

  const submitFree = (event: { preventDefault: () => void }): void => {
    event.preventDefault();
    const text = draft.trim();
    if (text === '') return;
    setFreeText(text);
    setParams({});
  };

  const suggestion = freeText ? closestPreset(freeText, presets) : null;
  const cropNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of crops.data ?? []) map.set(c.id, c.name);
    return map;
  }, [crops.data]);

  return (
    <Stack>
      <PageHeader
        level="district"
        title={t(m.pageTitle)}
        purpose={t(m.pagePurpose, { date: dateLong(asOf) })}
        actions={<EvidenceMark kind="demo" label={t(m.pageMark)} />}
      />

      <Card size="lg">
        <div className="ai-ask">
          <form className="ai-ask__bar" onSubmit={submitFree}>
            <Icon icon={Sparkles} size={18} />
            <input
              className="ai-ask__input"
              placeholder={t(m.askPlaceholder)}
              aria-label={t(m.askAria)}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <button type="submit" className="btn btn--secondary btn--sm focus-ring">
              {t(m.askButton)}
            </button>
          </form>

          {freeText && suggestion ? (
            <div className="ai-free" role="status">
              <div className="ai-free__title">{t(m.freeTitle)}</div>
              <p className="t-caption">{t(m.freeText, { text: freeText, question: suggestion.question })}</p>
              <div className="ai-free__actions">
                <button
                  type="button"
                  className="btn btn--secondary btn--sm focus-ring"
                  onClick={() => askPreset(suggestion.id)}
                >
                  {t(m.freeAsk)}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm focus-ring"
                  onClick={() => setFreeText(null)}
                >
                  {t(m.freeClose)}
                </button>
              </div>
            </div>
          ) : null}

          <div>
            <div className="ai-sub">{t(m.presetsTitle)}</div>
            <div className="ai-presets">
              {presets.map((preset) => (
                <button
                  type="button"
                  key={preset.id}
                  className={`ai-preset focus-ring ${asked === preset.id ? 'is-on' : ''}`}
                  aria-pressed={asked === preset.id}
                  onClick={() => askPreset(preset.id)}
                >
                  <span className="ai-preset__icon" aria-hidden="true">
                    <Icon icon={preset.icon} size={20} />
                  </span>
                  <span className="ai-preset__q">{preset.question}</span>
                  <span className="ai-preset__why">{preset.why}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {asked === 'case' ? (
        <Card
          title={t(m.pickTitle)}
          aside={
            contours.data ? (
              <span className="t-caption num">{countOf(contours.data.length, WORDS.contour)}</span>
            ) : null
          }
        >
          <div className="case-pick">
            <div className="case-pick__bar">
              <Icon icon={Search} size={18} />
              <input
                className="case-pick__input"
                placeholder={samples[0] ? t(m.pickPlaceholderSample, { no: samples[0].no }) : t(m.pickPlaceholder)}
                aria-label={t(m.pickAria)}
                value={pick}
                onChange={(e) => setPick(e.target.value)}
              />
              {caseId ? (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm focus-ring"
                  onClick={() => setParams({ preset: 'case' })}
                >
                  {t(m.pickCloseCase)}
                </button>
              ) : null}
            </div>

            {pick.trim() === '' ? (
              <Stack tight>
                <p className="t-caption">{t(m.pickHelp, { code: (chunks) => <code>{chunks}</code> })}</p>
                {samples.length > 0 ? (
                  <div className="chips">
                    <span className="t-caption">{t(m.pickSamples)}</span>
                    {samples.map((c) => (
                      <button
                        type="button"
                        key={c.id}
                        className="chip focus-ring"
                        onClick={() => openCase(c.id)}
                      >
                        {t(m.pickChip, { no: c.no })}
                      </button>
                    ))}
                  </div>
                ) : null}
              </Stack>
            ) : found.length === 0 ? (
              <EmptyState
                compact
                title={t(m.pickNoneTitle)}
                reason={t(m.pickNoneReason, { query: pick.trim() })}
              />
            ) : (
              <div className="case-pick__results">
                {found.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    className="case-pick__row focus-ring"
                    onClick={() => openCase(c.id)}
                  >
                    <span className="case-pick__no">{t(m.pickChip, { no: c.no })}</span>
                    <span className="case-pick__meta">
                      {t(m.pickRowMeta, {
                        farm: farms.data?.find((f) => f.id === c.farm_id)?.name ?? c.farm_id,
                        massif: massifs.data?.find((massif) => massif.id === c.massif_id)?.name ?? c.massif_id,
                        ha: ha(c.area_ha),
                        crop: c.crop_2026 ? cropNameById.get(c.crop_2026) ?? c.crop_2026 : t(m.pickNoPlacement),
                      })}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </Card>
      ) : null}

      {caseId ? <CaseContour contourId={caseId} asOf={asOf} /> : null}

      {asked && asked !== 'case' && !answer ? (
        <Card size="lg" title={t(m.notBuiltTitle)}>
          <EmptyState
            compact
            title={loading ? t(m.notBuiltLoading) : t(m.notBuiltMissing)}
            reason={loading ? t(m.notBuiltLoadingReason) : t(m.notBuiltMissingReason)}
          />
        </Card>
      ) : null}

      {answer ? <AnswerView answer={answer} asOf={asOf} /> : null}

      {!asked && !freeText ? (
        <Card title={t(m.howTitle)}>
          <ul className="opsnote">
            <li>{t(m.howForm)}</li>
            <li>{t(m.howClass)}</li>
            <li>{t(m.howCauses)}</li>
            <li>{t(m.howActions)}</li>
          </ul>
        </Card>
      ) : null}
    </Stack>
  );
}
